import type { ReactNode } from 'react'
import React, { useCallback, useContext } from 'react'
import moment from 'moment'
import { INTL_SHORT_DAY_NAME_DAY_MONTH } from 'constants/dateFormats'
import styled from 'styled-components'
import { SEVEN_DAYS_AFTER_PURCHASE, FOURTEEN_DAYS_BEFORE_CHECK_IN } from 'constants/cancellationPolicies'
import BodyText from 'LuxKit/Typography/BodyText'
import { getFlexibleCancellationDays, isCharterCancellationPolicy } from 'lib/offer/cancellationPolicyUtils'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import TextLink from 'LuxKit/TextLink'
import Clickable from 'components/Common/Clickable'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import ModalContext from 'contexts/ModalContext'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import OfferPropertyFlexibleCancellationPolicyModal from './OfferPropertyFlexibleCancellationPolicyModal'
import OfferPropertyFlexibleCancellationPolicyTimelineModal from './OfferPropertyFlexibleCancellationPolicyTimelineModal'
import OfferPropertyFlexibleCancellationCharterPolicyModal from './OfferPropertyFlexibleCancellationCharterPolicyModal'
import { useAppSelector } from 'hooks/reduxHooks'
import { isCharterEnabled } from 'selectors/featuresSelectors'
import { useIntl } from 'react-intl'
import Colour from 'components/utils/Colour'
import { OptimizelyExperiments } from 'constants/optimizely'
import { useDeferredAnalyticsOptimizely } from 'lib/optimizely/useDeferredAnalyticsOptimizely'

const MiddledTooltipIcon = styled(LineInfoCircleIcon)`
  vertical-align: middle;
`

interface Props {
  cancellationPolicy: App.OfferCancellationPolicy
  propertyTimezoneOffset?: number
  checkInDate?: string | moment.Moment
  isFlashUSP?: boolean
}

function OfferPropertyFlexibleCancellationPolicies(props: Props) {
  const { cancellationPolicy, propertyTimezoneOffset, checkInDate, isFlashUSP } = props
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()

  const days = getFlexibleCancellationDays(cancellationPolicy.type)
  let startBasedOnToday = propertyTimezoneOffset ? moment().utcOffset(propertyTimezoneOffset).add(SEVEN_DAYS_AFTER_PURCHASE, 'days') : moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days')
  let startBasedOnCheckIn = checkInDate && propertyTimezoneOffset ? moment(checkInDate).utcOffset(propertyTimezoneOffset).subtract(days, 'days') : moment(checkInDate).subtract(days, 'days')

  const changeOfMindDeadline = checkInDate && propertyTimezoneOffset ? moment(checkInDate).utcOffset(propertyTimezoneOffset).subtract(FOURTEEN_DAYS_BEFORE_CHECK_IN, 'days') : moment(checkInDate).subtract(FOURTEEN_DAYS_BEFORE_CHECK_IN, 'days')
  startBasedOnToday = startBasedOnToday.isBefore(changeOfMindDeadline) ? startBasedOnToday : changeOfMindDeadline

  const cancellationAfterChangeOfMindPeriod = !!(checkInDate && startBasedOnToday.isBefore(changeOfMindDeadline) && moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days').isBefore(moment(checkInDate).subtract(days, 'days')))
  startBasedOnCheckIn = cancellationAfterChangeOfMindPeriod ? startBasedOnCheckIn : startBasedOnToday

  const isCharterModeEnabled = useAppSelector(isCharterEnabled)
  const isCharterPolicy = isCharterCancellationPolicy(cancellationPolicy)
  const charterPolicyApplies = isCharterPolicy && isCharterModeEnabled
  const showModal = useContext(ModalContext)
  const {
    variation: cancellationPolicyTimelineVariation,
    triggerAnalytics: triggerTimelineAnalytics,
  } = useDeferredAnalyticsOptimizely(OptimizelyExperiments.croCancellationPolicyModalTimeline)
  const isTimelineModalEnabled = !!cancellationPolicyTimelineVariation

  const onItemClick = useCallback<React.MouseEventHandler<HTMLAnchorElement>>((e) => {
    e.preventDefault()
    triggerTimelineAnalytics()
    const today = propertyTimezoneOffset ? moment().utcOffset(propertyTimezoneOffset) : moment()

    if (isTimelineModalEnabled) {
      showModal(
        <OfferPropertyFlexibleCancellationPolicyTimelineModal
          today={today}
          checkInDate={checkInDate}
          startBasedOnToday={startBasedOnToday}
          startBasedOnCheckIn={startBasedOnCheckIn}
          cancellationAfterChangeOfMindPeriod={cancellationAfterChangeOfMindPeriod}
          days={days}
          isCharter={charterPolicyApplies}
        />,
      )
    } else if (charterPolicyApplies) {
      showModal(
        <OfferPropertyFlexibleCancellationCharterPolicyModal
          checkInDate={checkInDate}
          startBasedOnToday={startBasedOnToday}
          startBasedOnCheckIn={startBasedOnCheckIn}
          cancellationAfterChangeOfMindPeriod={cancellationAfterChangeOfMindPeriod}
          days={days}
        />,
      )
    } else {
      showModal(
        <OfferPropertyFlexibleCancellationPolicyModal
          checkInDate={checkInDate}
          startBasedOnToday={startBasedOnToday}
          startBasedOnCheckIn={startBasedOnCheckIn}
          cancellationAfterChangeOfMindPeriod={cancellationAfterChangeOfMindPeriod}
          days={days}
        />,
      )
    }
  }, [
    showModal,
    startBasedOnToday,
    startBasedOnCheckIn,
    cancellationAfterChangeOfMindPeriod,
    checkInDate,
    days,
    charterPolicyApplies,
    isTimelineModalEnabled,
    triggerTimelineAnalytics,
    propertyTimezoneOffset,
  ])

  const hotelOnlyText = !charterPolicyApplies
    ? intl.formatMessage({
        id: '3hgb7knLZn',
        defaultMessage: '(Hotel only)',
        description: 'Text indicating hotel-only cancellation policy',
      })
    : ''

  let cancellationPolicyText: ReactNode = null

  if (checkInDate && startBasedOnCheckIn) {
    cancellationPolicyText = intl.formatMessage({
      id: 'vkyGHlbfCK',
      defaultMessage: '<bold>Flexible cancellation</bold> until {date} {hotelOnlyText}',
      description: 'Text for flexible cancellation until a specific date',
    },
    {
      date: intlDateFormatter(startBasedOnCheckIn, INTL_SHORT_DAY_NAME_DAY_MONTH),
      hotelOnlyText,
      bold: chunks => <Colour value="highlight-secondary" as="b">{chunks}</Colour>,
    })
  } else if (!checkInDate) {
    cancellationPolicyText = intl.formatMessage({
      id: 'FegGnf9HkD',
      defaultMessage: '<bold>Flexible cancellation</bold> up to 7 days after purchase {hotelOnlyText}',
      description: 'Text for flexible cancellation up to 7 days after purchase',
    },
    {
      hotelOnlyText,
      bold: chunks => <Colour value="highlight-secondary" as="b">{chunks}</Colour>,
    })
  } else {
    cancellationPolicyText = intl.formatMessage({
      id: 'B3mlG0sXHi',
      defaultMessage: '<bold>Flexible cancellation</bold>',
      description: 'Label for flexible cancellation policy',
    }, {
      bold: chunks => <Colour value="highlight-secondary" as="b">{chunks}</Colour>,
    })
  }

  return <>
    {!isFlashUSP &&
      <Clickable onClick={onItemClick}>
        <BodyText variant="medium" as="span">
          {cancellationPolicyText}{' '}
          <MiddledTooltipIcon size="XS" />
        </BodyText>
      </Clickable>}
    {isFlashUSP &&
      <TextLink
        size="medium"
        colour="highlight-secondary"
        underline={false}
        data-type="7day"
        onClick={onItemClick}
        startIcon={<LineRefundIcon />}
      >
        {intl.formatMessage({
          id: '0jG3dGkr8T',
          defaultMessage: 'Flexible cancellation',
          description: 'Link text for flexible cancellation policy',
        })}
      </TextLink>}
  </>
}

export default OfferPropertyFlexibleCancellationPolicies
